import { HttpApiEndpoint, HttpApiError, HttpApiGroup } from "@effect/platform";
import { Schema } from "effect";
import { DataIntegrityError, DatabaseError } from "../auth/errors";
import { Authorization } from "../auth/middleware";
import { UserGroupId } from "../ids/definitions";
import { MongoUserId } from "../ids/mongo-ids";

export const UserGroupResponse = Schema.Struct({
  id: UserGroupId,
  name: Schema.String,
  description: Schema.NullOr(Schema.String),
  parentId: Schema.NullOr(UserGroupId),
  /** Member user ids, populated by the list endpoint. */
  mongoUserIds: Schema.Array(MongoUserId),
});
export type UserGroupResponse = typeof UserGroupResponse.Type;

const ListUserGroupsResponse = Schema.Struct({
  groups: Schema.Array(UserGroupResponse),
});

const CreateUserGroupInput = Schema.Struct({
  name: Schema.String.pipe(Schema.minLength(1)),
  description: Schema.optional(Schema.NullOr(Schema.String)),
  parentId: Schema.optional(Schema.NullOr(UserGroupId)),
  /** Optional initial members to add after the group is created. */
  mongoUserIds: Schema.optional(Schema.Array(MongoUserId)),
});

const CreateUserGroupResponse = Schema.Struct({ group: UserGroupResponse });

const UpdateUserGroupInput = Schema.Struct({
  name: Schema.optional(Schema.String.pipe(Schema.minLength(1))),
  description: Schema.optional(Schema.NullOr(Schema.String)),
  parentId: Schema.optional(Schema.NullOr(UserGroupId)),
});

const ListUserGroupMembersResponse = Schema.Struct({
  mongoUserIds: Schema.Array(MongoUserId),
});

const AddUserGroupMembersInput = Schema.Struct({
  mongoUserIds: Schema.Array(MongoUserId).pipe(Schema.minItems(1)),
});

export const UserGroupGroup = HttpApiGroup.make("userGroup")
  .add(
    HttpApiEndpoint.get("listUserGroups", "/user-groups")
      .middleware(Authorization)
      .addSuccess(ListUserGroupsResponse)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    HttpApiEndpoint.post("createUserGroup", "/user-groups")
      .middleware(Authorization)
      .setPayload(CreateUserGroupInput)
      .addSuccess(CreateUserGroupResponse)
      .addError(HttpApiError.BadRequest)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    HttpApiEndpoint.patch("updateUserGroup", "/user-groups/:id")
      .middleware(Authorization)
      .setPath(Schema.Struct({ id: UserGroupId }))
      .setPayload(UpdateUserGroupInput)
      .addSuccess(Schema.Void)
      .addError(HttpApiError.BadRequest)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    HttpApiEndpoint.del("deleteUserGroup", "/user-groups/:id")
      .middleware(Authorization)
      .setPath(Schema.Struct({ id: UserGroupId }))
      .addSuccess(Schema.Void)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    HttpApiEndpoint.get("listUserGroupMembers", "/user-groups/:id/members")
      .middleware(Authorization)
      .setPath(Schema.Struct({ id: UserGroupId }))
      .addSuccess(ListUserGroupMembersResponse)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    HttpApiEndpoint.post("addUserGroupMembers", "/user-groups/:id/members")
      .middleware(Authorization)
      .setPath(Schema.Struct({ id: UserGroupId }))
      .setPayload(AddUserGroupMembersInput)
      .addSuccess(Schema.Void)
      .addError(HttpApiError.BadRequest)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    HttpApiEndpoint.del(
      "removeUserGroupMember",
      "/user-groups/:id/members/:mongoUserId",
    )
      .middleware(Authorization)
      .setPath(Schema.Struct({ id: UserGroupId, mongoUserId: MongoUserId }))
      .addSuccess(Schema.Void)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .prefix("/api");
